/**
 * Icon Utilities
 * 
 * Utility functions for rendering Lucide icons dynamically
 */

import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Category, CategoryType } from '../types/category.types';

/**
 * Get a Lucide icon component by name
 */
const isLucideIconExport = (value: unknown): value is LucideIcon => {
  if (!value) return false;
  if (typeof value === 'function') return true;
  if (typeof value === 'object') {
    return 'render' in (value as Record<string, unknown>) || 'displayName' in (value as Record<string, unknown>);
  }
  return false;
};

export function getIconComponent(iconName?: string): LucideIcon | null {
  if (!iconName) return null;
  
  // Convert kebab-case or snake_case to PascalCase (e.g., 'shopping-cart' -> 'ShoppingCart')
  const toPascalCase = (str: string): string => {
    return str
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
  };
  
  // Try different variations
  const variations = [
    toPascalCase(iconName), // 'shopping-cart' -> 'ShoppingCart'
    iconName.charAt(0).toUpperCase() + iconName.slice(1), // 'home' -> 'Home'
    iconName, // Original name
    iconName.toUpperCase(), // 'HOME'
  ];
  
  for (const name of variations) {
    const Icon = (LucideIcons as any)[name];
    if (isLucideIconExport(Icon)) {
      return Icon as LucideIcon;
    }
  }
  
  // Fallback to folder icon
  const FolderIcon = (LucideIcons as any)['Folder'] || (LucideIcons as any)['FolderOpen'];
  return isLucideIconExport(FolderIcon) ? (FolderIcon as LucideIcon) : null;
}

const CATEGORY_ICON_FALLBACKS: Record<string, string> = {
  housing: 'Home',
  transportation: 'Car',
  utilities: 'Plug',
  groceries: 'ShoppingCart',
  'dining-hospitality': 'Utensils',
  healthcare: 'HeartPulse',
  insurance: 'Shield',
  education: 'GraduationCap',
  'entertainment-lifestyle': 'Sparkles',
  'personal-care': 'Sparkles',
  'children-family': 'Baby',
  pets: 'Dog',
  'gifts-donations': 'Gift',
  'savings-investments': 'PiggyBank',
  'debt-interest': 'Wallet',
  taxes: 'Receipt',
  'business-expenses': 'Briefcase',
  'professional-development': 'BookOpenCheck',
  travel: 'Plane',
  income: 'Coins',
  transfers: 'ArrowLeftRight',
};

const toSlug = (value?: string): string | undefined => {
  if (!value) return undefined;
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export function resolveCategoryIconName(category: Pick<Category, 'icon' | 'slug' | 'name' | 'type'>): string | undefined {
  if (category.icon) {
    return category.icon;
  }

  const slug = category.slug || toSlug(category.name);
  if (slug && CATEGORY_ICON_FALLBACKS[slug]) {
    return CATEGORY_ICON_FALLBACKS[slug];
  }

  if (category.type === CategoryType.INCOME) {
    return 'Coins';
  }

  if (category.type === CategoryType.TRANSFER) {
    return 'ArrowLeftRight';
  }

  return 'Folder';
}

export function getCategoryIconComponent(category: Pick<Category, 'icon' | 'slug' | 'name' | 'type'>): LucideIcon | null {
  const iconName = resolveCategoryIconName(category);
  return getIconComponent(iconName);
}

